import './App.scss'
import { useContext, useEffect } from 'react'
import { BrowserRouter, Route, Routes } from 'react-router-dom'
import { GoabAppHeader,    GoabCircularProgress,    GoabOneColumnLayout,  GoabPageBlock, GoabTemporaryNotificationCtrl } from '@abgov/react-components'
import {  appBuildLabel, appEnv, appStrings, appVersionLabel } from './common/constants'
import { Home } from './pages/home/Home'
import { Nav } from './common/components/nav/Nav'
import { TicketCtrl } from './pages/ticketCtrl/TicketCtrl'
import { useAuth } from 'react-oidc-context'
import { AppContext } from './common/contexts/apiContext'
import ApiService from './sevices/ApiService'
import TicketService from './sevices/TicketService'
import { wdpUtil } from './common/utils/util'
import { appLog } from './common/utils/app-log'
import { arePrerequisitesReady } from './common/utils/lookupReadiness'

import { VersionBar } from 'shared-ui-component';
import "shared-ui-component/dist/index.css";
import { ErrorPage } from './pages/errPage/ErrorPage'
import { LoggedOut } from './pages/loggedOut/LoggedOut'

//##############################################################
function App(_props: any) {
  
  const auth = useAuth();
  const myAppCtx = useContext(AppContext);
  //const { flagsLoading, hasFlag } = useFeatureFlags();
  
  /********************************************************************************************
   * 
  ********************************************************************************************/
  useEffect(() => {
    const isLoggedOutPath = window.location.pathname === "/logged-out";
    if (!isLoggedOutPath && !auth.isLoading && !auth.isAuthenticated) {
      if (window.location.search) {
        window.location.href = window.location.origin;
      } else {
        auth.clearStaleState();
        auth.signinRedirect();
      }
    }
    // else if (!isLoggedOutPath && !auth.isLoading && auth.isAuthenticated) {
    //   auth.startSilentRenew();
    // }
  }, [auth]);
  /********************************************************************************************
   * 
  ********************************************************************************************/
  useEffect(() => {
    // this is for GoA customization/overwrite 
    const goaH = document.querySelectorAll('goa-app-header')[0];
    if (goaH !== undefined) {
      let shadow = goaH?.shadowRoot;
      const link: HTMLAnchorElement | undefined = shadow?.querySelectorAll('a')[0];
      if (link !== undefined) {
        link.target = '_blank';
      }
    }
  }, []);
  /********************************************************************************************
   * Load prerequisite lookups once at app level so all routes/components can consume them.
  ********************************************************************************************/
  useEffect(() => {
    // Guard 1: wait until auth is ready and context is available.
    if (!auth.isAuthenticated || auth.isLoading || wdpUtil.isNullorUndefined(myAppCtx)) {
      return;
    }

    // Guard 2: skip network call if prerequisites are already in shared context.
    const cacheReady = arePrerequisitesReady(myAppCtx!.lookups);
    if (cacheReady) {
      return;
    }

    let isActive = true;
    // Inner function to allow async/await inside useEffect.
    const loadPrerequisites = async () => {
      try {
        // Step 1: load prerequisite datasets from the API.
        const res: any = await ApiService.loadPrerequisite(auth.user?.access_token as string);

        // Step 2: stop if effect was cleaned up or response is empty.
        if (!isActive || res == null) {
          return;
        }

        // Step 3: derive forest area list from lookup relations.
        res.forestAreaList = TicketService.extractFromRelations(res.genericRelationsList, 'forestArea');

        // Step 4: cache lookups in app context for all pages.
        myAppCtx?.setLookups({
          contractorsList: res.contractorsList,
          equipmentTypeList: res.equipmentTypeList,
          ticketStatusList: res.ticketStatusList,
          resourceTypeList: res.resourceTypeList,
          costRateTypeList: res.costRateTypeList,
          costRateUnitList: res.costRateUnitList,
          genericRelationsList: res.genericRelationsList,
          forestAreaList: res.forestAreaList,
          workPerformedRelationsList: res.workPerformedRelationsList,
        });
      } catch (error: any) {
        appLog.error(`Loading Prerequisites: ${error.message}`);
      }
    };

    // Start loading prerequisites.
    loadPrerequisites();

    return () => {
      // Prevent state updates if the component unmounts mid-request.
      isActive = false;
    };
  }, [auth.isAuthenticated,auth.isLoading,auth.user?.access_token]);
  /********************************************************************************************
   * 
  ********************************************************************************************/
  function getPortalUrl(): string {
    let url = appStrings.portalUrlDev;
    if (appEnv === 'dev' || appEnv === 'local') {
      url = appStrings.portalUrlDev;
    }
    else if (appEnv === 'uat') {
      url = appStrings.portalUrlUat;
    }
    else if (appEnv === 'prod') {
      url = appStrings.portalUrlProd;
    }
    return url;
  }
  // function getMenuTitle() {
  //   let title = 'Init ver';
  //   return title;
  // }
  /********************************************************************************************
   * 
  ********************************************************************************************/
  // function getMenuTitle() {
  //   let title = '';
  //   //if (hasFlag('Incidents')) {
  //   if(true){
  //     title = 'Incidents Dashboard';
  //   }
  //   return title;
  // }
  /********************************************************************************************
   * Main HTML section
  ********************************************************************************************/
  return (
    <>
      {/* single global container - call TemporaryNotification.show()/dismiss() from anywhere to trigger notifications */}
      <GoabTemporaryNotificationCtrl />
      {!auth.isLoading && <div className='mainContainer'>
        <VersionBar 
          environment= {appEnv} 
          environmentLabel={appEnv} 
          versionLabel={appVersionLabel} 
          buildLabel={appBuildLabel}
        />
      </div>
      }
      <BrowserRouter>
        <GoabOneColumnLayout>
          <section className="my-appHeader" >
            <GoabAppHeader url={getPortalUrl()} heading="Time Tracking" maxContentWidth="100%"
              utilities={<>
                <div className='my-appNav'>
                  {!auth.isLoading && <Nav />}
                </div>
              </>}
            />
          </section>
          <GoabPageBlock width="100%">
            {!auth.isLoading &&
              <Routes>
                {<Route path='/' element={<Home />} />}
                {<Route path='/ticket/:digitalId' element={<TicketCtrl />} />}
                {<Route path='/ticket-ctrl/:ticketId/:ticketNumber?' element={<TicketCtrl />} />} 
                <Route path='/logged-out' element={<LoggedOut />} />
                <Route path="*" element={<ErrorPage />} />
                {/* {<Route path='/my-account' element={<MyAccount />} />} */}
                {/* <Route path='/errorPage/:errorCode?' element={<ErrorPage />} /> */}
              </Routes>
            }
            {(auth.isLoading) && <GoabCircularProgress variant="fullscreen" size="large" message="Authentication in progress..." visible={true} />} 
          </GoabPageBlock>
        </GoabOneColumnLayout>
      </BrowserRouter>
    </>
  );
}

export default App
